/**
 * Talking to the account endpoints.
 *
 * A plain module with no rune in it, for exactly the reason `settings-io.ts`
 * gives: `$state` only exists once the Svelte compiler has been over a file,
 * so anything that has to run under plain Vitest lives here and the reactive
 * wrapper lives in `account.svelte.ts`. This is what
 * `test/state/account-io.test.ts` drives, against a stub `fetch`.
 *
 * ## Three rules this module encodes
 *
 * 1. **Nothing here ever throws for a reason a player caused.** Every function
 *    returns a result. A 401, a 503, a dead network and a browser in
 *    aeroplane mode are all ordinary answers to "am I signed in", and the
 *    caller is a screen, not an error boundary.
 * 2. **`unavailable` is a first-class outcome.** A deployment with no database
 *    answers 503, and so does one whose database is briefly unreachable. The
 *    UI says "accounts are unavailable" and offers guest play, which is the
 *    default anyway.
 * 3. **No token is ever handled here.** The session is an `httpOnly` cookie
 *    the browser attaches by itself — `credentials: 'same-origin'` is the
 *    whole of it. The one token this file does touch is the short-lived socket
 *    ticket, which exists precisely because the cookie is unreadable, and it
 *    is fetched, used and forgotten inside one call. See
 *    `apps/server/src/accounts/service.ts`.
 */

import type { LocalSession } from '@interlock/protocol';

/** The response shape shared by `/api/auth/me` and the merge routes. */
export interface AccountBest {
  readonly modeKey: string;
  readonly value: number;
  readonly betterWhen: 'lower' | 'higher';
  readonly formatted: string;
  readonly achievedAtMs: number;
}

export interface AccountPlayer {
  readonly id: string;
  readonly username: string;
  readonly createdAtMs: number;
}

export interface AccountRecord {
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface AccountProfile {
  readonly player: AccountPlayer;
  readonly record: AccountRecord;
  readonly bests: readonly AccountBest[];
  readonly settings: unknown;
  /**
   * Whether the server considers this account an administrator.
   *
   * It decides whether the admin panel is *offered*, and nothing else. Every
   * `/api/admin/*` route asks the same question again, server-side, from the
   * session — so a client that set this to `true` by hand would get a screen
   * full of 403s, which is exactly the outcome that shape is chosen for.
   * Optional in the type because an older server does not send it.
   */
  readonly isAdmin?: boolean;
}

export type AccountOutcome<T> =
  | { readonly status: 'ok'; readonly value: T }
  /** No session. Not an error — it is what a guest looks like. */
  | { readonly status: 'signed-out' }
  /**
   * Signed in, and not allowed. Only the admin routes can answer this, and it
   * is a separate outcome from `refused` because it is the one refusal a
   * screen responds to by showing a *different screen* rather than a message.
   */
  | { readonly status: 'forbidden' }
  /** No database here, or the database is unreachable. Guest play is unaffected. */
  | { readonly status: 'unavailable' }
  /** A refusal worth showing: a taken username, a wrong password, a rate limit. */
  | { readonly status: 'refused'; readonly message: string }
  /** The request never completed — offline, or the server is not there. */
  | { readonly status: 'offline' };

export const AUTH_REGISTER = '/api/auth/register';
export const AUTH_LOGIN = '/api/auth/login';
export const AUTH_LOGOUT = '/api/auth/logout';
export const AUTH_ME = '/api/auth/me';
export const AUTH_TICKET = '/api/auth/ticket';
export const ACCOUNT_BESTS = '/api/account/bests';
export const ACCOUNT_SETTINGS = '/api/account/settings';
export const ACCOUNT_PASSWORD = '/api/account/password';

/** Injected in tests; defaults to the page's own `fetch`. */
export type Fetcher = typeof globalThis.fetch;

export interface RequestOptions {
  readonly method: string;
  readonly body?: unknown;
  readonly fetcher?: Fetcher;
}

/**
 * The one request function.
 *
 * Exported so `admin-io.ts` uses it rather than growing a second copy: `credentials: 'same-origin'`, the status-to-outcome
 * mapping and the "never throws for a reason a player caused" rule are one
 * decision each and must not be made twice.
 */
export async function request<T>(
  path: string,
  options: RequestOptions,
): Promise<AccountOutcome<T>> {
  const fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  let response: Response;
  try {
    response = await fetcher(path, {
      method: options.method,
      // Same-origin by construction (`docs/DEPLOYMENT.md` requirement 3), and
      // stated rather than relied on: this is what carries the session cookie.
      credentials: 'same-origin',
      ...(options.body === undefined
        ? {}
        : {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(options.body),
          }),
    });
  } catch {
    // The network, not the server. An offline install lands here on every
    // load and must land here silently.
    return { status: 'offline' };
  }

  if (response.status === 401) return { status: 'signed-out' };
  if (response.status === 403) return { status: 'forbidden' };
  if (response.status === 503) return { status: 'unavailable' };

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message = (payload as { error?: unknown } | null)?.error;
    return {
      status: 'refused',
      message: typeof message === 'string' ? message : 'That did not work. Try again.',
    };
  }
  return { status: 'ok', value: payload as T };
}

export function register(
  username: string,
  password: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ player: AccountPlayer }>> {
  return request(AUTH_REGISTER, {
    method: 'POST',
    body: { username, password },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

export function login(
  username: string,
  password: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ player: AccountPlayer }>> {
  return request(AUTH_LOGIN, {
    method: 'POST',
    body: { username, password },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

export function logout(fetcher?: Fetcher): Promise<AccountOutcome<{ ok: true }>> {
  return request(AUTH_LOGOUT, {
    method: 'POST',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

export function fetchProfile(fetcher?: Fetcher): Promise<AccountOutcome<AccountProfile>> {
  return request(AUTH_ME, {
    method: 'GET',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

/** Merge a device's local bests up, and take back whatever now stands. */
export function pushBests(
  bests: Readonly<Record<string, unknown>>,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ bests: readonly AccountBest[] }>> {
  return request(ACCOUNT_BESTS, {
    method: 'POST',
    body: { bests },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

export function pushSettings(
  settings: unknown,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ settings: unknown }>> {
  return request(ACCOUNT_SETTINGS, {
    method: 'PUT',
    body: { settings },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

/**
 * A one-minute, single-use token for the game socket's join options.
 *
 * Returns `null` for every failure — signed out, no accounts, offline — and
 * that is the point rather than a shortcut: the caller is about to join a duel
 * either way, as a guest if it has to. A duel that refused to start because a
 * ticket endpoint was slow would be exactly the degradation
 * `docs/DESIGN.md`'s accounts amendment forbids.
 */
/**
 * Replace the signed-in player's password. The server asks for the current
 * one as well as the session, answers `forbidden` when it is wrong, and signs
 * every other device out when it is right.
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ ok: true; sessionsRevoked: number }>> {
  return request(ACCOUNT_PASSWORD, {
    method: 'POST',
    body: { currentPassword, newPassword },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

export async function fetchSocketTicket(fetcher?: Fetcher): Promise<string | null> {
  const result = await request<{ ticket: string }>(AUTH_TICKET, {
    method: 'POST',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
  return result.status === 'ok' && typeof result.value.ticket === 'string'
    ? result.value.ticket
    : null;
}

export const ACCOUNT_LOCAL_SESSIONS = '/api/account/local-sessions';

/**
 * Merge this device's rounds on one device up (`docs/decisions/0059`), and
 * take back the account's, newest first. Kept apart from every other route
 * here: it carries no best, no statistic and no daily result.
 */
export function pushLocalSessions(
  sessions: readonly LocalSession[],
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ sessions: readonly LocalSession[] }>> {
  return request(ACCOUNT_LOCAL_SESSIONS, {
    method: 'PUT',
    body: { sessions },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}
